import { NodeId, TreeView } from '@fluid-experimental/tree';
import { Serializable } from '@fluidframework/datastore-definitions';
import { OrderedListReaderType, ElementId, OrderedListElement, ELEMENTS } from '.';

import { assertPrecondition, mfsError } from '../../../errors';
import { findParentCollection } from '../lookup';
import { logVerbose } from '../../../log';

export class OrderedListReader<T> implements OrderedListReaderType<T> {
  constructor(protected view: TreeView, protected root: ElementId) {}

  /** @inheritdoc */
  public get(position: ElementId): Serializable<T> {
    logVerbose('get', 'get on specified elementId position', { elementId: position });
    this.validateElementId(position);
    return this.view.getViewNode(position).payload as Serializable<T>;
  }

  /** @inheritdoc */
  public *getElements(parentId?: ElementId, isReverse = false): IterableIterator<OrderedListElement<T>> {
    logVerbose('getElements', 'get elements', { parentId, isReverse });
    for (const position of this.getPositions(parentId, isReverse)) {
      yield { elementValue: this.get(position), elementId: position, parentId: parentId ?? this.root };
    }
  }

  /** @inheritdoc */
  public hasElement(elementId: ElementId, root?: NodeId): boolean {
    logVerbose('hasElement', 'check has element', { elementId, root });
    const parentCollection = findParentCollection(this.view, elementId);
    return parentCollection?.identifier === (root ?? this.root);
  }

  /**
   * Gets the positions of all or a sub-tree of list elements parented under a certain element at level-1.
   *
   * @param parentId If specified, gets only elements that are parented under parentId.
   */
  protected getPositions(parentId?: ElementId, isReverse = false): readonly ElementId[] {
    this.validateElementId(parentId);

    const view = this.view;

    const nodeIds = view.getTrait({
      parent: parentId ?? this.root,
      label: ELEMENTS,
    });

    return isReverse ? nodeIds.slice().reverse() : nodeIds;
  }

  /**
   * Validate if the `elementId` refers to a valid snapshot node (exists as an element of the current collection). When
   * `allowNotFound` is true, not-existing nodes are valid (to enable idempotent remove element operations).
   */
  protected validateElementId(elementId?: ElementId, allowNotFound = false, root?: NodeId): boolean {
    if (elementId === undefined) {
      return true;
    }

    if (!this.view.hasNode(elementId)) {
      logVerbose('validateElementId', `elementId: ${elementId} does not exist.`);
      if (allowNotFound) {
        return false;
      }

      throw mfsError.invalidPosition();
    }

    assertPrecondition(this.hasElement(elementId, root), mfsError.invalidPosition);
    return true;
  }
}
